import { Box, Paper, Stack, Typography } from "@mui/material";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { CostSummary } from "../../shared/Analytics.ts";
import { Stage } from "../../shared/CaseConfiguration";
import { BargainingData, PartyDescriptors } from "../../shared/CaseModel";
import { getSettledSummary } from "../../shared/CaseModelUtility";
import CenteredMessageBox from "../general/CenteredMessageBox";
import { Metric } from "../general/dataAtoms/Metric";
import { NO_VALUE, signedTone } from "../general/dataAtoms/shared";
import Loading from "../general/Loading";
import { SectionLabel } from "../general/SectionLabel";
import { SegmentedControl } from "../general/SegmentedControl";
import { Tabs } from "../general/Tabs";
import { TonalBadge } from "../general/TonalBadge";
import { byName, clientsFirst, sort } from "./AnalyticsUtility";
import { getEvChartDataForParty, StageData } from "./EvAnalyticsUtility";
import { EvChart, EvChartDataOfStage } from "./EvCostStackedBarChart";
import { EvMatrix } from "./EvMatrix";
import { SettledEmptyState } from "./settlement/SettledEmptyState";
import { judgmentOf } from "../../shared/judgment";
import { JudgedState } from "../timeline/JudgedState";
import { GuideHeader } from "../guide/GuideHeader";
import { settlingAt } from "../../shared/adjustedView";
import { useLitigation } from "./ShownResults";
import { useVsLitigation, VsLitigationLine } from "./VsLitigation";

/** The height of the chart with its legend, in px. */
const CHART_HEIGHT = 400;

function initializeStageData(
  evMatrix: EvMatrix,
  parties: PartyDescriptors,
  sortedParties: string[],
  stages: Stage[],
): StageData[] {
  const partiesWithEvs = evMatrix.getPartyIds();
  if (partiesWithEvs.length === 0) {
    return [];
  }
  return (
    stages
      // Appeals is still hidden: we have a very simplified handling of it and it disturbs the chart more than it helps.
      .filter((stage) => stage !== Stage.Appeals)
      .map((stage) => ({
        stage,
        data: sortedParties.map((partyId) => {
          const party = parties[partyId];
          return {
            partyId,
            partyName: party.name,
            client: party.isClient || false,
            value:
              evMatrix.getOwnEvs(partyId)?.find((ev) => ev.stage === stage)
                ?.value ?? NaN,
          };
        }),
      }))
  );
}

export const EvAnalytics = () => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "EVAnalytics" });
  const { t: tCase } = useTranslation(undefined, { keyPrefix: "case" });
  const caseContext = useCase();

  const evMatrix = caseContext.getEvMatrix();
  const parties = caseContext.getPartyDescriptors();
  const stages = caseContext.getStages();
  const c = caseContext.getCase();
  const bargainingConfiguration = c.variants?.bargaining;
  const bargainingResults = bargainingConfiguration
    ? caseContext.getVariantResults(bargainingConfiguration.id)
    : undefined;

  const bargainingData: BargainingData[] =
    bargainingResults?.getBargainingData() ?? [];

  // defines order in charts etc.
  const sortedParties: string[] = useMemo(
    () => sort(parties, clientsFirst, byName),
    [parties],
  );

  // holds all parties' EV data, per stage, in order of the case stages
  const perStageData: StageData[] = useMemo(
    () => initializeStageData(evMatrix, parties, sortedParties, stages),
    [evMatrix, parties, sortedParties, stages],
  );

  // in the settlement-adjusted view, each stage set against litigation
  const litigation = useLitigation();
  const adjusted = c.settlementAdjusted;
  // in the litigation view, the values with settlements, which the scale
  // spans too
  const adjustedValuesOf = (partyId: string) =>
    adjusted?.status === "done"
      ? (adjusted.parties
          .find((party) => party.partyId === partyId)
          ?.average.stageEvs.map(({ ev }) => ev) ?? [])
      : [];
  const vsLitigationOf = (
    partyId: string,
  ): VsLitigationOfStages | undefined => {
    if (litigation === caseContext || adjusted?.status !== "done")
      return undefined;
    const litigated = litigation.getEvMatrix().getOwnEvs(partyId);
    return {
      otherValues: (litigated ?? []).map(({ value }) => value),
      litigatedAt: (stage) =>
        litigated?.find((ev) => ev.stage === stage)?.value,
      settlingAt: (stage) =>
        settlingAt(adjusted.pairs, partyId, stage).map(
          ({ opponentId, ...chances }) => ({
            name: parties[opponentId]?.name ?? opponentId,
            ...chances,
          }),
        ),
    };
  };

  // A fully settled case has no stage-by-stage value left to show: state the
  // agreed result, as Bargaining does, rather than an empty case value page.
  if (judgmentOf(c)) return <JudgedState />;
  const settled = getSettledSummary(c);
  if (settled) return <SettledEmptyState caseId={c.id} {...settled} />;

  if (!bargainingConfiguration)
    return <CenteredMessageBox message={t("error.noData")} />;

  if (!bargainingResults) {
    if (caseContext.variantResultsLoading) return <Loading />;
    return <CenteredMessageBox message={t("error.noData")} />;
  }

  if (perStageData.length === 0)
    return <CenteredMessageBox message={t("error.noData")} />;

  const tabs: { label: ReactNode; tabComponent: ReactNode }[] =
    sortedParties.map((partyId) => {
      const party = parties[partyId];
      return {
        label: (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <span>{party.name ?? partyId}</span>
            {party.isClient && (
              <TonalBadge tone={theme.palette.primary}>
                {tCase("parties.clientBadge")}
              </TonalBadge>
            )}
          </Stack>
        ),
        tabComponent: (
          <CaseValueOfParty
            partyId={partyId}
            perStageData={perStageData}
            parties={parties}
            costSummary={caseContext.getCostSummaryOf(partyId)}
            bargainingData={bargainingData}
            vsLitigation={vsLitigationOf(partyId)}
            otherValues={
              vsLitigationOf(partyId)?.otherValues ?? adjustedValuesOf(partyId)
            }
          />
        ),
      };
    });

  return (
    <Stack spacing={1.5} sx={{ userSelect: "none" }}>
      {/* the header, and at its end which results the page shows */}
      <GuideHeader>
        <Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 700, letterSpacing: "-.01em" }}
          >
            {t("title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.5, maxWidth: "76ch" }}
          >
            {t("intro")}
          </Typography>
        </Box>
      </GuideHeader>
      <Box>
        <Tabs tabs={tabs} />
      </Box>
    </Stack>
  );
};

/**
 * What the case is worth to a party stage by stage, with the costs of the
 * party under it and, on request, where it would settle with its opponents.
 */
export const CaseValueOfParty = ({
  partyId,
  perStageData,
  costSummary,
  parties,
  bargainingData,
  vsLitigation,
  otherValues,
}: {
  partyId: string;
  perStageData: StageData[];
  costSummary: CostSummary;
  parties: PartyDescriptors;
  bargainingData: BargainingData[];
  vsLitigation?: VsLitigationOfStages;
  // the values of the other view, which the scale spans too
  otherValues?: number[];
}) => {
  const { currency } = useCurrency();
  const lines = useVsLitigation();
  const { t } = useTranslation(undefined, { keyPrefix: "EVAnalytics" });
  const [view, setView] = useState<"evOnly" | "midpoints">("evOnly");

  const evChartData: EvChartDataOfStage[] = useMemo(() => {
    const data = getEvChartDataForParty(
      partyId,
      perStageData,
      parties,
      costSummary,
      view === "evOnly" ? [] : bargainingData,
    );
    // skip starting stages with same EV to not clutter the chart
    // but include the last stage in that sequence.
    const firstEv = data[0].expectedValue;
    let breakIndex = 0;
    for (; breakIndex < data.length; breakIndex++) {
      if (
        data[breakIndex].expectedValue !== firstEv ||
        !data[breakIndex].estimatedCost.eq(0)
      )
        break;
    }
    const startStageIndex = breakIndex > 0 ? breakIndex - 1 : 0;
    // Build each bar's label here, in one place. The first bar may represent a
    // packed run of equal-value stages; label it as a range from the first
    // packed stage (data[0]) to its own stage. Other bars get a single label.
    return data.slice(startStageIndex).map(({ stage, ...rest }, index) => {
      const fromStage =
        index === 0 && startStageIndex > 0 ? data[0].stage : stage;
      const label =
        fromStage === stage
          ? t("stages." + Stage[stage])
          : t("stageRange", {
              from: t("stages." + Stage[fromStage]),
              to: t("stages." + Stage[stage]),
            });
      return {
        ...rest,
        label,
        ...(vsLitigation && {
          litigated: vsLitigation.litigatedAt(stage),
          settling: vsLitigation.settlingAt(stage),
        }),
      };
    });
  }, [
    partyId,
    perStageData,
    parties,
    costSummary,
    bargainingData,
    view,
    t,
    vsLitigation,
  ]);

  // the figures over the chart are those of its last bar
  const lastStage = evChartData[evChartData.length - 1];
  const costsByLastStage = lastStage.incurredCost
    .plus(lastStage.estimatedCost)
    .toNumber();

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, px: 2.5, pt: 2, pb: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <SectionLabel component="h2">{t("evChartPaper.title")}</SectionLabel>
        <SegmentedControl
          value={view}
          onChange={setView}
          options={[
            { value: "evOnly", label: t("evChartPaper.toggle.evOnly") },
            {
              value: "midpoints",
              label: t("evChartPaper.toggle.settlementMidpoints"),
            },
          ]}
        />
      </Box>
      <Stack
        direction="row"
        useFlexGap
        sx={{ flexWrap: "wrap", columnGap: 4.25, rowGap: 1.5, mt: 1.75 }}
      >
        <Metric
          label={t("evChartPaper.expectedValueAt", { stage: lastStage.label })}
          // a party without an expected value at the stage has no figure
          value={
            Number.isNaN(lastStage.expectedValue)
              ? NO_VALUE
              : currency.format(lastStage.expectedValue)
          }
          tone={
            Number.isNaN(lastStage.expectedValue)
              ? undefined
              : signedTone(lastStage.expectedValue)
          }
          // in both views a line under it, so switching moves nothing
          {...captionOf(
            lastStage.litigated === undefined ||
              Number.isNaN(lastStage.expectedValue)
              ? undefined
              : lines.change(lastStage.expectedValue - lastStage.litigated),
          )}
        />
        <Metric
          label={t("evChartPaper.costsByThen")}
          value={currency.format(costsByLastStage)}
          caption={"\u00a0"}
        />
      </Stack>
      <Box sx={{ height: CHART_HEIGHT, mt: 2 }}>
        <EvChart stages={evChartData} extent={otherValues} />
      </Box>
      <Typography
        variant="caption"
        component="p"
        sx={{ mt: 1, color: "text.secondary" }}
      >
        {t(
          vsLitigation
            ? "evChartPaper.explanationAdjusted"
            : "evChartPaper.explanation",
        )}
      </Typography>
    </Paper>
  );
};

/** What a stage of a party is set against in the settlement-adjusted view. */
type VsLitigationOfStages = {
  // the values in litigation, which the scale spans too
  otherValues: number[];
  litigatedAt: (stage: Stage) => number | undefined;
  settlingAt: (
    stage: Stage,
  ) => { name: string; byThen: number; here: number }[];
};

/** The caption of a figure against litigation, or an empty line. */
const captionOf = (line: VsLitigationLine | undefined) => ({
  caption: line?.text ?? "\u00a0",
  captionTone: line?.tone,
});
